<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import AppNavigation from '@/components/AppNavigation.vue'
import { useNavigation } from '@/composables/useNavigation'
import { useAuth } from '@/stores/auth'
import { useDarkMode } from '@/composables/useDarkMode'
import { usePrefs } from '@/stores/prefs'
import logo from '@/assets/logo-fbmi.jpg'

const { t } = useI18n()
const auth = useAuth()
const prefs = usePrefs()
const darkMode = useDarkMode()
const { hasPanel } = useNavigation()
const { smAndDown } = useDisplay()
/** On a wide screen the panel opens as last left; on a phone it starts closed and closes when the window narrows. */
const navigation = ref(!smAndDown.value && prefs.navigation)
watch(smAndDown, (small) => {
    navigation.value = !small && prefs.navigation
})
function toggleNavigation() {
    navigation.value = !navigation.value
    if (!smAndDown.value) {
        prefs.setNavigation(navigation.value)
    }
}
const otherLocale = computed(() => (prefs.locale === 'cs' ? 'en' : 'cs'))
const displayName = computed(() => auth.me?.fullName ?? auth.me?.username ?? '')
const initials = computed(() =>
    displayName.value
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase(),
)
</script>

<template>
    <!-- A flat bar on the primary container: light blue in the light theme, deep blue in the dark one. -->
    <v-app-bar class="app-shell__bar" color="primary-container" density="comfortable" flat>
        <template #prepend>
            <v-app-bar-nav-icon
                v-if="hasPanel"
                data-test="navigation-toggle"
                :aria-label="t('nav.menu')"
                :aria-expanded="navigation ? 'true' : 'false'"
                @click="toggleNavigation"
            />
            <router-link :to="{ name: 'courses' }" data-test="home-link" class="app-shell__home" :aria-label="t('nav.home')">
                <img :src="logo" alt="FBMI ČVUT" height="32" class="app-shell__logo" />
            </router-link>
        </template>
        <v-app-bar-title class="app-shell__title">
            {{ t('app.title') }}
        </v-app-bar-title>
        <template #append>
            <v-btn
                data-test="locale-toggle"
                class="app-shell__locale"
                variant="text"
                prepend-icon="mdi-translate"
                :aria-label="t('nav.language')"
                @click="prefs.setLocale(otherLocale)"
            >
                {{ otherLocale.toUpperCase() }}
            </v-btn>
            <v-menu location="bottom end" :close-on-content-click="false">
                <template #activator="{ props }">
                    <v-btn v-bind="props" data-test="user-menu" class="app-shell__user ml-1" variant="tonal" color="primary">
                        <v-avatar start size="26" color="primary" class="app-shell__avatar">
                            {{ initials }}
                        </v-avatar>
                        <span data-test="user-name" class="d-none d-sm-inline">{{ displayName }}</span>
                    </v-btn>
                </template>
                <v-list min-width="280">
                    <v-list-item :subtitle="auth.roles.map((role) => t(`roles.${role}`)).join(', ')" :title="auth.me?.username" />
                    <v-divider class="my-1" />
                    <v-list-item
                        data-test="theme-toggle"
                        prepend-icon="mdi-theme-light-dark"
                        :title="t('nav.darkMode')"
                        @click="darkMode.toggle()"
                    >
                        <template #append>
                            <v-switch
                                :model-value="darkMode.isDark.value"
                                color="primary"
                                density="compact"
                                hide-details
                                inset
                                readonly
                                tabindex="-1"
                            />
                        </template>
                    </v-list-item>
                    <v-list-item data-test="logout" prepend-icon="mdi-logout" :title="t('nav.logout')" @click="auth.logout()" />
                </v-list>
            </v-menu>
        </template>
    </v-app-bar>
    <AppNavigation v-if="hasPanel" v-model="navigation" />
    <v-main class="app-shell__main">
        <router-view />
    </v-main>
</template>

<style scoped>
.app-shell__bar {
    border-block-end: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.app-shell__home {
    display: block;
    border-radius: 8px;
}

.app-shell__logo {
    display: block;
    box-sizing: content-box;
    margin-inline-start: 12px;
    padding: 4px 6px;
    border-radius: 8px;
    /* The logo is a JPEG on white: a white tile makes that edge deliberate on the coloured bar. */
    background: #fff;
}

.app-shell__title {
    font-size: 1.0625rem;
    font-weight: 500;
    letter-spacing: 0;
}

.app-shell__avatar {
    font-size: 0.6875rem;
    font-weight: 600;
}

.app-shell__main {
    background: rgb(var(--v-theme-background));
}
</style>
